import { CloudArrowUp, File, Warning } from '@phosphor-icons/react';
import { useRef, useState } from 'react';
import { api } from '../lib/api';
import { Modal } from './Modal';

interface UploadModalProps {
  open: boolean;
  authenticated: boolean;
  onClose: () => void;
  onRequireLogin: () => void;
  onUploaded: (name: string, url: string) => void;
}

const MAX_SIZE = Number(import.meta.env.VITE_MAX_MODEL_SIZE_BYTES || 3 * 1024 * 1024);

export function UploadModal({ open, authenticated, onClose, onRequireLogin, onUploaded }: UploadModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const selectFile = (next: File | undefined) => {
    setError('');
    if (!next) return;
    if (!/\.(glb|gltf)$/i.test(next.name)) { setError('GLB 또는 GLTF 파일만 등록할 수 있습니다.'); return; }
    if (next.size > MAX_SIZE) { setError(`파일은 ${(MAX_SIZE / 1024 / 1024).toFixed(0)}MB 이하여야 합니다.`); return; }
    setFile(next);
  };
  const upload = async () => {
    if (!authenticated) { onClose(); onRequireLogin(); return; }
    if (!file || !consent) return;
    setLoading(true);
    setError('');
    try {
      const result = await api.uploadModel(file, consent);
      onUploaded(result.asset.name, result.asset.url);
      onClose();
      setFile(null);
      setConsent(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '모델을 등록하지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <Modal open={open} onClose={onClose} title="내 3D 모델 등록" description="베타 기간에는 모든 등록 모델이 공개 라이브러리에 함께 제공됩니다." className="upload-modal">
      <div className="beta-notice"><Warning size={21} weight="fill" /><div><strong>베타 공개 등록 안내</strong><p>베타 버전에서는 업로드한 모델과 파일명이 모든 사용자에게 공개됩니다. 다른 사용자가 검색하고 배치할 수 있는 파일인지 확인해 주세요.</p><p className="beta-private-note">정식 버전에서는 유료 요금제 사용자에게 비공개 Private 스토리지를 제공할 예정입니다.</p></div></div>
      <button className={`file-drop ${file ? 'has-file' : ''}`} type="button" onClick={() => inputRef.current?.click()} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); selectFile(event.dataTransfer.files[0]); }}>
        {file ? <File size={28} weight="duotone" /> : <CloudArrowUp size={30} />}
        <span><strong>{file?.name ?? 'GLB 또는 GLTF 파일 선택'}</strong><small>{file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : `끌어 놓기 가능 · 최대 ${(MAX_SIZE / 1024 / 1024).toFixed(0)}MB`}</small></span>
        <input ref={inputRef} type="file" accept=".glb,.gltf,model/gltf-binary,model/gltf+json" onChange={(event) => selectFile(event.target.files?.[0])} />
      </button>
      <label className="consent-check"><input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} /><span>이 모델을 공개 라이브러리에 등록하는 데 동의합니다.</span></label>
      <p className="future-note">GLTF는 텍스처가 파일 안에 포함된 형태만 지원합니다. 정식 버전에서는 유료 계정에 비공개 오브젝트 세트가 제공될 예정입니다.</p>
      {error && <p className="form-error" role="alert">{error}</p>}
      <button className="button button-primary button-block" type="button" onClick={upload} disabled={loading || !file || !consent}>
        <CloudArrowUp size={18} />{loading ? 'R2에 업로드 중...' : authenticated ? '모델 등록' : '로그인하고 등록'}
      </button>
    </Modal>
  );
}
